import { zodResolver } from "@hookform/resolvers/zod";
import React from "react";
import { useForm } from "react-hook-form";
import toast from "react-hot-toast";
import { useNavigate } from "react-router-dom";
import { z } from "zod";
import { setCookie } from "../../config/auth";
import { API_ENDPOINTS } from "../../config/api-endpoints";
import { usePost } from "../../packages/hooks";
import { selectAuthorize, useAuthStore } from "../../store/useAuth";
import { AdminUser, LoginRequest, LoginResponse } from "../../types/api";
import { setTokenInStorage } from "../../utils/token-utils";
import "./login.css";

// Login form schema
const loginSchema = z.object({
  username: z.string().min(1, "Логин обязателен"),
  password: z.string().min(1, "Пароль обязателен"),
});

type LoginFormData = z.infer<typeof loginSchema>;

const Login: React.FC = () => {
  const navigate = useNavigate();
  const authorize = useAuthStore(selectAuthorize);

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<LoginFormData>({
    resolver: zodResolver(loginSchema),
  });

  // Login mutation
  const loginMutation = usePost<LoginResponse, LoginRequest>(
    API_ENDPOINTS.AUTH.LOGIN,
    {
      onSuccess: async (data) => {
        // Extract user data and token from the nested response structure
        const { token, user } = data.data;

        // Create admin user object from API response
        const admin: AdminUser = {
          id: user.id,
          email: user.email,
          role: user.role,
          branchId: user.branchId,
          telegramId: user.telegramId,
          telegramFirstName: user.telegramFirstName,
          telegramLastName: user.telegramLastName,
          telegramUsername: user.telegramUsername,
          telegramLanguageCode: user.telegramLanguageCode,
          telegramPhotoUrl: user.telegramPhotoUrl,
          referrerId: user.referrerId,
          origin: user.origin,
          createdAt: user.createdAt,
          updatedAt: user.updatedAt,
          branch: user.branch,
        };

        // Store token in localStorage using utility function
        setTokenInStorage(token);

        // Authorize user and set token in store
        authorize(admin, token);

        // Set auth token cookie for middleware
        setCookie("authToken", token);

        toast.success("Успешный вход в систему!");

        // Navigate to dashboard after successful login
        navigate("/");
      },
      onError: (error: any) => {
        console.error("Login error:", error);

        let errorMessage = "Вход не удался. Проверьте учетные данные.";

        // Handle different error types
        if (error.status === 401) {
          errorMessage = "Неверный логин или пароль";
        } else if (error.status === 403) {
          errorMessage = "Доступ запрещен";
        } else if (error.status === 404) {
          errorMessage = "Сервер авторизации недоступен";
        } else if (error.status === 500) {
          errorMessage = "Ошибка сервера. Попробуйте позже";
        } else if (error.status === 0 || !navigator.onLine) {
          errorMessage = "Нет подключения к интернету";
        } else if (error.message) {
          errorMessage = error.message;
        }

        toast.error(errorMessage);
      },
    }
  );

  const onSubmit = async (
    data: LoginFormData,
    event?: React.BaseSyntheticEvent
  ) => {
    event?.preventDefault(); // stop full page reload
    await loginMutation.mutateAsync(data);
  };

  return (
    <div className="login">
      <form className="login__form" onSubmit={handleSubmit(onSubmit)}>
        <h2 className="login__title">Вход</h2>

        <div className="login__input-group">
          <input
            type="text"
            placeholder="Логин"
            className={`login__input ${errors.username ? "error" : ""}`}
            {...register("username")}
          />
          {errors.username && (
            <span className="error-message">{errors.username.message}</span>
          )}
        </div>

        <div className="login__input-group">
          <input
            type="password"
            placeholder="Пароль"
            className={`login__input ${errors.password ? "error" : ""}`}
            autoComplete="current-password"
            {...register("password")}
          />
          {errors.password && (
            <span className="error-message">{errors.password.message}</span>
          )}
        </div>

        <button
          type="submit"
          className="login__button"
          disabled={isSubmitting || loginMutation.isPending}
        >
          {isSubmitting || loginMutation.isPending ? "Вход..." : "Войти"}
        </button>
      </form>
    </div>
  );
};

export default Login;
